import { Trans } from "@lingui/react/macro"
import { IconHistory } from "@tabler/icons-react"
import { useSpeakerColor } from "../../../../contexts/SpeakerColors"
import { identityTextColor } from "../../../../lib/speaker-colors"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

function OwnerBadge({ name }: { name: string }): React.JSX.Element {
  const color = useSpeakerColor(name)
  return (
    <span className={classes.followUpOwner} style={{ color: identityTextColor(color) }}>
      {name}
    </span>
  )
}

/**
 * FollowUpCard renders carry-over items from the previous meeting in this
 * series — resolved / open / dropped, with optional note. The parent only
 * mounts this card when `analysis.follow_up` is non-null, so we don't
 * guard against that here.
 *
 * Each row's status pill maps:
 *   resolved      → ok
 *   carried_over  → warning
 *   dropped       → muted
 *
 * Sits in the right-hand insights rail with the `warning` rail variant
 * so previously-tense / open business reads at a glance.
 */
export function FollowUpCard({ followUp }: IProps): React.JSX.Element | null {
  const items = followUp.items
  if (!items || items.length === 0) return null
  const dateOnly = String(followUp.previous_meeting_date ?? "").split("T")[0]
  return (
    <section className={classes.insight}>
      <div className={classes.insightHead}>
        <IconHistory size={16} color="var(--mantine-color-warning-5)" />
        <h3>
          <Trans>Follow-up</Trans>
        </h3>
        <span className={classes.insightTag} data-tone="warning">
          {items.length}
        </span>
      </div>
      {followUp.previous_meeting_subject && (
        <p className={classes.insightBody}>
          <Trans>From</Trans> <span className={classes.followUpSubject}>{followUp.previous_meeting_subject}</span>
          {dateOnly && ` · ${dateOnly}`}
        </p>
      )}
      <ul className={classes.followUpList}>
        {items.map((item, i) => {
          const tone = item.status === "resolved" ? "ok" : item.status === "carried_over" ? "warning" : "muted"
          return (
            <li key={`${i}-${item.description}`} className={classes.followUpRow}>
              <span className={classes.followUpStatus} data-tone={tone}>
                {item.status === "resolved" && <Trans>Done</Trans>}
                {item.status === "carried_over" && <Trans>Open</Trans>}
                {item.status === "dropped" && <Trans>Dropped</Trans>}
              </span>
              <div className={classes.followUpBody}>
                <p className={classes.followUpDesc}>
                  {item.owner && <OwnerBadge name={item.owner} />}
                  {item.description}
                </p>
                {item.note && <p className={classes.followUpNote}>{item.note}</p>}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
